import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "./components/ThemeProvider";
import ProtectedRoute from "./components/ProtectedRoute";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import ErrorBoundary from "./components/ErrorBoundary";
// Temporarily disabled - causing performance issues
// import { RealtimeNotificationListener } from "./components/RealtimeNotificationListener";

// Lazy load pages for better performance (code splitting)
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Book = lazy(() => import("./pages/Book"));
const SalonPage = lazy(() => import("./pages/SalonPage"));
const MyAppointments = lazy(() => import("./pages/MyAppointments"));
const SalonOwnerDashboard = lazy(() => import("./pages/SalonOwnerDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));
const InstallGuide = lazy(() => import("./pages/InstallGuide"));
const Promo = lazy(() => import("./pages/Promo"));
const EmployeeDashboard = lazy(() => import("./pages/EmployeeDashboard"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

const App = () => (
  <ErrorBoundary>
    <ThemeProvider defaultTheme="light" storageKey="toronet-theme">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
        <Toaster />
        <Sonner />
        {/* Temporarily disabled - causing performance issues */}
        {/* <RealtimeNotificationListener /> */}
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <PWAInstallPrompt />
          <Suspense fallback={<PageLoader />}>
            <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/salon/:salonId" element={<SalonPage />} />
            <Route path="/book" element={<Book />} />
            <Route path="/book/:salonId" element={<Book />} />
            <Route 
              path="/my-appointments" 
              element={
                <ProtectedRoute>
                  <MyAppointments />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/salon-owner" 
              element={
                <ProtectedRoute requiredRole="salon_owner">
                  <SalonOwnerDashboard />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute requiredRole="manager_or_admin">
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/employee" 
              element={
                <ProtectedRoute>
                  <EmployeeDashboard />
                </ProtectedRoute>
              } 
            />
              <Route path="/install-guide" element={<InstallGuide />} />
              <Route path="/promo" element={<Promo />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;
